import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { record } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';
import { io, Socket } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');
const LIVE_FLUSH_MS = 300; // Flush live events to Socket.IO every 300ms for near-real-time
const FLUSH_INTERVAL_MS = 12000; // Send events every 12 seconds
const MAX_BATCH_SIZE = 500; // Max events per batch to avoid huge payloads
const IDLE_TIMEOUT_MS = 120000; // 2 minutes of no activity = idle (30s was too aggressive)
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

interface SessionRecorderProps {
  applicationId: string;
}

const SessionRecorder: React.FC<SessionRecorderProps> = ({ applicationId }) => {
  const location = useLocation();
  const recordingIdRef = useRef<string | null>(null);
  const eventsBufferRef = useRef<eventWithTime[]>([]);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAdminMode = useRef(false);
  const isMountedRef = useRef(true);
  const isFlushingRef = useRef(false);
  const isIdleRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live streaming refs (parallel path — does NOT affect HTTP recording)
  const liveSocketRef = useRef<Socket | null>(null);
  const liveBufferRef = useRef<eventWithTime[]>([]);
  const liveFlushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentPage = location.pathname;

  // Start rrweb capture engine
  const startRrwebCapture = useCallback(() => {
    if (stopRecordingRef.current) return; // already recording
    const stop = record({
      emit(event: eventWithTime) {
        if (isMountedRef.current && !isIdleRef.current) {
          eventsBufferRef.current.push(event);
          // Also push to live buffer for real-time Socket.IO streaming (parallel path)
          liveBufferRef.current.push(event);
          // Immediately send snapshot/meta events to server for reliable caching (not batched)
          if ((event.type === 2 || event.type === 4) && liveSocketRef.current?.connected) {
            liveSocketRef.current.emit('live-session-snapshot', { applicationId, event });
          }
        }
      },
      checkoutEveryNms: 120000, // full snapshot every 2min (was 5s — caused massive bloat)
      sampling: {
        mousemove: 75, // capture mouse every 75ms (smooth enough, 3x less data than 25ms)
        mouseInteraction: true,
        scroll: 150,
        input: 'last', // capture final input value (not every keystroke)
      },
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // only mask password fields
      },
      blockClass: 'sr-block',
      maskTextClass: 'sr-mask',
      recordCanvas: false, // disabled — large data overhead, enable only if needed
      collectFonts: false, // let replay use system fonts (saves ~100KB per snapshot)
      inlineStylesheet: true,
      inlineImages: false, // replay fetches images from URLs (was inlining ~6MB of PNGs per snapshot!)
      slimDOMOptions: {
        script: true,
        comment: true,
        headFavicon: false,
        headWhitespace: true,
        headMetaDescKeywords: false, // keep meta for modal/overlay CSS
        headMetaSocial: true,
        headMetaRobots: true,
        headMetaHttpEquiv: false, // keep charset/viewport meta
        headMetaAuthorship: true,
        headMetaVerification: true,
      },
    });
    if (stop) {
      stopRecordingRef.current = stop;
      console.log('[SessionRecorder] rrweb capture started');
    }
  }, [applicationId]);

  // Stop rrweb capture engine
  const stopRrwebCapture = useCallback(() => {
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }
  }, []);

  // Flush buffered events to the server
  const flushEvents = useCallback(async () => {
    if (isFlushingRef.current) return;
    if (!recordingIdRef.current || eventsBufferRef.current.length === 0) return;

    isFlushingRef.current = true;
    const eventsToSend = eventsBufferRef.current.splice(0, MAX_BATCH_SIZE);

    try {
      await fetch(`${API_BASE}/api/session-recording/events`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recordingId: recordingIdRef.current,
          events: eventsToSend,
          currentPage,
        }),
      });
    } catch (err) {
      eventsBufferRef.current.unshift(...eventsToSend);
      console.warn('[SessionRecorder] Failed to flush events, will retry:', err);
    } finally {
      isFlushingRef.current = false;
    }
  }, [currentPage]);

  // Handle idle detection - pause recording when no activity
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // If was idle, resume recording
    if (isIdleRef.current) {
      isIdleRef.current = false;
      console.log('[SessionRecorder] Activity detected, resuming capture');
      startRrwebCapture();
    }

    // Set new idle timeout
    idleTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      isIdleRef.current = true;
      console.log('[SessionRecorder] User idle, pausing capture');
      // Flush any remaining events before pausing
      flushEvents();
      stopRrwebCapture();
    }, IDLE_TIMEOUT_MS);
  }, [startRrwebCapture, stopRrwebCapture, flushEvents]);

  // Start the recording session
  const startSession = useCallback(async () => {
    if (!applicationId || isAdminMode.current) return;

    try {
      const res = await fetch(`${API_BASE}/api/session-recording/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId, currentPage }),
      });
      const data = await res.json();
      if (data.success && data.recordingId) {
        recordingIdRef.current = data.recordingId;
        console.log(`[SessionRecorder] Session ${data.resumed ? 'resumed' : 'started'}: ${data.recordingId}`);

        // Start rrweb capture
        startRrwebCapture();

        // Start periodic flush
        flushTimerRef.current = setInterval(() => {
          if (!isIdleRef.current) {
            flushEvents();
          }
        }, FLUSH_INTERVAL_MS);
      }
    } catch (err) {
      console.error('[SessionRecorder] Failed to start session:', err);
    }
  }, [applicationId, currentPage, flushEvents, startRrwebCapture]);

  // Initialize live Socket.IO streaming (parallel to HTTP recording)
  const startLiveStream = useCallback(() => {
    if (liveSocketRef.current || !applicationId) return;
    try {
      const sock = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });
      liveSocketRef.current = sock;

      const userEmail = sessionStorage.getItem('userEmail') || '';
      sock.on('connect', () => {
        sock.emit('live-session-start', { applicationId, userEmail, currentPage });
        console.log('[SessionRecorder] Live stream socket connected');

        // Force a fresh snapshot after a short delay to ensure it's sent while socket IS connected.
        // This fixes the race condition where rrweb's initial snapshot fires before the socket connects.
        setTimeout(() => {
          try {
            if (stopRecordingRef.current && typeof (record as any).takeFullSnapshot === 'function') {
              (record as any).takeFullSnapshot();
              console.log('[SessionRecorder] Forced initial snapshot for live stream after socket connect');
            }
          } catch (e) {
            console.warn('[SessionRecorder] Initial snapshot force failed:', e);
          }
        }, 800);
      });

      // Admin requests a fresh snapshot — trigger rrweb to take one
      sock.on('take-snapshot', () => {
        console.log('[SessionRecorder] Admin requested fresh snapshot, triggering takeFullSnapshot');
        try {
          if (typeof (record as any).takeFullSnapshot === 'function') {
            (record as any).takeFullSnapshot();
          }
        } catch (e) {
          console.warn('[SessionRecorder] takeFullSnapshot failed:', e);
        }
      });

      // Periodically flush live buffer via Socket.IO
      liveFlushTimerRef.current = setInterval(() => {
        if (liveBufferRef.current.length > 0 && liveSocketRef.current?.connected) {
          const batch = liveBufferRef.current.splice(0);
          liveSocketRef.current.emit('live-session-events', { applicationId, events: batch });
        }
      }, LIVE_FLUSH_MS);
    } catch (e) {
      console.warn('[SessionRecorder] Live stream init failed:', e);
    }
  }, [applicationId, currentPage]);

  const stopLiveStream = useCallback(() => {
    if (liveFlushTimerRef.current) {
      clearInterval(liveFlushTimerRef.current);
      liveFlushTimerRef.current = null;
    }
    if (liveSocketRef.current) {
      try {
        liveSocketRef.current.emit('live-session-stop', { applicationId });
        liveSocketRef.current.disconnect();
      } catch (e) { /* best-effort */ }
      liveSocketRef.current = null;
    }
    liveBufferRef.current = [];
  }, [applicationId]);

  // Initialize on mount
  useEffect(() => {
    isMountedRef.current = true;
    const adminEditMode = sessionStorage.getItem('adminEditMode');
    isAdminMode.current = adminEditMode === 'true';

    if (!isAdminMode.current && applicationId) {
      startSession();
      startLiveStream();
    }

    // Cleanup on unmount — stop recording and notify server
    return () => {
      isMountedRef.current = false;

      // Stop live stream (parallel path cleanup)
      stopLiveStream();

      // Clear idle timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }

      // Stop rrweb
      stopRrwebCapture();

      // Clear flush timer
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      // Final flush + stop notification with keepalive
      const rid = recordingIdRef.current;
      const remaining = eventsBufferRef.current.splice(0);
      if (rid) {
        if (remaining.length > 0) {
          try {
            fetch(`${API_BASE}/api/session-recording/events`, {
              method: 'POST', credentials: 'include', keepalive: true,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ recordingId: rid, events: remaining, currentPage }),
            }).catch(() => {});
          } catch (e) { /* best-effort */ }
        }
        // Stop the recording on server
        try {
          fetch(`${API_BASE}/api/session-recording/stop`, {
            method: 'POST', credentials: 'include', keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ recordingId: rid, status: 'completed' }),
          }).catch(() => {});
        } catch (e) { /* best-effort */ }
        recordingIdRef.current = null;
      }
    };
  }, [applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Emit page changes to live stream socket
  useEffect(() => {
    if (liveSocketRef.current?.connected && applicationId) {
      liveSocketRef.current.emit('live-session-page', { applicationId, currentPage });
    }
  }, [currentPage, applicationId]);

  // Idle detection: listen for user activity
  useEffect(() => {
    if (!recordingIdRef.current && !applicationId) return;

    // Start idle timer immediately
    resetIdleTimer();

    const onActivity = () => resetIdleTimer();

    ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, onActivity));
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [resetIdleTimer, applicationId]);

  // Handle page visibility change (user switches tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushEvents();
      }
    };

    const handleBeforeUnload = () => {
      const rid = recordingIdRef.current;
      const remaining = eventsBufferRef.current.splice(0);
      if (rid && remaining.length > 0) {
        try {
          fetch(`${API_BASE}/api/session-recording/events`, {
            method: 'POST', credentials: 'include', keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ recordingId: rid, events: remaining, currentPage }),
          }).catch(() => {});
        } catch (e) { /* best-effort */ }
      }
      if (rid) {
        try {
          fetch(`${API_BASE}/api/session-recording/stop`, {
            method: 'POST', credentials: 'include', keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ recordingId: rid, status: 'completed' }),
          }).catch(() => {});
        } catch (e) { /* best-effort */ }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushEvents, currentPage]);

  return null;
};

export default SessionRecorder;
